import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  LayoutDashboard, Calendar, Users, Settings, 
  FileText, MapPin, LogOut, Menu, X, DollarSign,
  CreditCard, BarChart2, Plane, Link2, Mail,
  Clock, MessageSquare, Send, Tag, Code, TrendingUp, Shield, ShoppingCart, List
} from 'lucide-react';
import { useState } from 'react';

const AdminSidebar = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('vipgez_admin_token');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/login-goturkeytransfer');
  };

  const navItems = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/bookings', icon: Calendar, label: 'Bookings' },
    { to: '/admin/abandoned', icon: ShoppingCart, label: 'Terk Edilenler' },
    { to: '/admin/customers', icon: Users, label: 'Customers' },
    { to: '/admin/feedback', icon: MessageSquare, label: 'Feedback' },
    { to: '/admin/routes', icon: MapPin, label: 'Popular Routes' },
    { to: '/admin/routes-list', icon: List, label: 'Routes List' },
    { to: '/admin/pricing', icon: DollarSign, label: 'Kâr Kuralları' },
    { to: '/admin/fx-rate', icon: TrendingUp, label: 'Döviz Kuru' },
    { to: '/admin/coupons', icon: Tag, label: 'Coupons' },
    { to: '/admin/airports', icon: Plane, label: 'Airports' },
    { to: '/admin/pages', icon: FileText, label: 'CMS Pages' },
    { to: '/admin/payment-methods', icon: CreditCard, label: 'Payment Methods' },
    { to: '/admin/tracking', icon: Code, label: 'Tracking & Pixels' },
    { to: '/admin/settings/integrations', icon: Link2, label: 'API & Integrations' },
    { to: '/admin/settings/email', icon: Mail, label: 'Email Settings' },
    { to: '/admin/settings/email-logs', icon: Send, label: 'Email Logs' },
    { to: '/admin/settings/cron', icon: Clock, label: 'Cron Monitor' },
    { to: '/admin/reports', icon: BarChart2, label: 'Reports' },
    { to: '/admin/fraud', icon: Shield, label: 'Fraud Detection' },
    { to: '/admin/settings', icon: Settings, label: 'Hesap Ayarları' },
  ];

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-border">
        <img
          src="https://customer-assets.emergentagent.com/job_27fa83fe-13fc-43b1-921c-bb11a32684e7/artifacts/9w38znq2_Go-Turkey-Transfer-logo.png"
          alt="GoTurkeyTransfer"
          className="h-8"
        />
        <p className="text-xs text-muted-foreground mt-1">Admin Panel</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setIsOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`
            }
          >
            <item.icon className="h-5 w-5" />
            <span className="text-sm">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-border">
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-muted-foreground hover:bg-red-500/10 hover:text-red-500 transition-colors"
        >
          <LogOut className="h-5 w-5" />
          <span className="text-sm">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Prevent admin pages from being indexed */}
      <Helmet prioritizeSeoTags>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
        <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet" />
        <meta name="bingbot" content="noindex, nofollow" />
      </Helmet>
      
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 left-4 z-50 p-2 bg-secondary rounded-lg lg:hidden"
        data-testid="mobile-menu-btn"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-secondary border-r border-border flex flex-col transform transition-transform duration-200 lg:transform-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <NavContent />
      </aside>
    </>
  );
};

export default AdminSidebar;
